<template>
  <v-container
    fluid
    class="pa-0"
  >
    <v-row>
      <v-col
        cols="12"
        md="6"
        lg="4"
      >
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="Store Name"
        >
          <v-text-field
            v-model="storeNameLocal"
            label="Store Name"
            suffix=".myshopify.com"
            :error-messages="errors"
            :disabled="!isNew"
          />
        </ValidationProvider>
      </v-col>
      <v-col
        cols="12"
        md="6"
        lg="4"
      >
        <v-select
          v-model="appTypeLocal"
          :items="appTypes"
          label="App Type"
        />
        <div class="caption">
          Starting in 2022 private apps have been deprecated.
          As of 2026 custom apps have been deprecated.
          All new implementations should use dev apps.
        </div>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'private'"
        cols="12"
        md="6"
        lg="4"
      >
        <ValidationProvider
          ref="apiKeyValidation"
          v-slot="{ errors }"
          rules="required"
          name="API Key"
        >
          <v-text-field
            v-model="apiKeyLocal"
            label="API Key"
            :error-messages="errors"
          />
        </ValidationProvider>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'private'"
        cols="12"
        md="6"
        lg="4"
      >
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          name="Password"
        >
          <v-text-field
            v-model="passwordLocal"
            label="Password"
            :error-messages="errors"
          />
        </ValidationProvider>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'custom'"
        cols="12"
        md="6"
        lg="4"
      >
        <ValidationProvider
          ref="accessTokenValidation"
          v-slot="{ errors }"
          rules="required"
          name="AccessToken"
        >
          <v-text-field
            v-model="accessTokenLocal"
            label="Access Token"
            :error-messages="errors"
          />
        </ValidationProvider>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'dev'"
        cols="12"
        md="6"
        lg="4"
      >
        <ValidationProvider
          ref="clientIdValidation"
          v-slot="{ errors }"
          rules="required"
          name="Client Id"
        >
          <v-text-field
            v-model="clientIdLocal"
            label="Client Id"
            :error-messages="errors"
          />
        </ValidationProvider>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'dev'"
        cols="12"
        md="6"
        lg="4"
      >
        <ValidationProvider
          ref="clientSecretValidation"
          v-slot="{ errors }"
          rules="required"
          name="Client Secret"
        >
          <v-text-field
            v-model="clientSecretLocal"
            label="Client Secret"
            :error-messages="errors"
          />
        </ValidationProvider>
      </v-col>
      <v-col
        v-if="appTypeLocal !== 'dev' || isNew === false"
        cols="12"
        md="6"
        lg="4"
      >
        <v-select
          v-model="ignoredInventoryLocationsLocal"
          :items="locations"
          item-text="name"
          item-value="id"
          label="Ignored Inventory Locations"
          clearable
          persistent-hint
          multiple
        />
        <div class="caption mt-3">
          Selected locations will not have their inventory sent to any destinations.
        </div>
      </v-col>
      <v-col
        v-if="appTypeLocal !== 'dev'"
        cols="12"
        md="6"
        lg="4"
      >
        <v-checkbox
          v-model="isGqlSalespersonFetchEnabledLocal"
          label="Include Salesperson"
        />
        <div class="caption mt-3">
          The app must be created and installed on a Shopify Plus or Advanced store, and
          the owner must contact Shopify support to enable <i>read_users</i> access.
        </div>
      </v-col>
    </v-row>
    <oauth
      v-if="appTypeLocal === 'dev'"
      ref="oauthControl"
      :is-new="isNew"
    />
    <v-row>
      <v-col
        v-if="appTypeLocal === 'custom'"
        cols="12"
        md="6"
      >
        To enable the pulling of data a custom app must be created in the shopify store. Follow
        these steps to enable that.
        <ol>
          <li>From your Shopify admin, go to Apps.</li>
          <li>Click Develop apps.</li>
          <li>Click Allow custom app development.</li>
          <li>
            Read the warning and information provided, and then click Allow custom app development.
          </li>
          <li>Click Create a custom app.</li>
          <li>
            In the modal window, enter the App name and select an App developer. The app developer
            can be the store owner, or any staff or collaborator account with the Develop apps
            permission.
          </li>
          <li>Click Create app.</li>
          <li>Click configure Admin API scopes.</li>
          <li>
            Give the following permissions
            <ul>
              <li>read_customers</li>
              <li>read_inventory</li>
              <li>read_locations</li>
              <li>read_orders</li>
              <li>read_products</li>
            </ul>
          </li>
          <li>Click Save.</li>
          <li>Click Install app.</li>
          <li>In the modal window, click Install app.</li>
          <li>Under Admin API access token click reveal token once</li>
          <li>Copy the token into the access token field above.</li>
        </ol>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'dev'"
        cols="12"
        md="6"
      >
        To enable the pulling of data a dev app must be created in the shopify dev portal. Follow
        these steps to enable that.
        <ol>
          <li>
            Go to <a
              href="https://dev.shopify.com"
              target="_blank"
            >https://dev.shopify.com</a>
          </li>
          <li>Login and you will be on your dashboard.</li>
          <li>Click Create app.</li>
          <li>Enter the App name and click create.</li>
          <li>Click Upper Quadrant in top right and click Partner Dashboard</li>
          <li>Click on the name of the app you just created</li>
          <li>Click Choose distribution</li>
          <li>Choose Custom distribution</li>
          <li>
            Enter the name of the myshopify domain of the store you are creating this app
            for and click Generate link
          </li>
          <li>Click API access requests on the left side</li>
          <li>Click Request access button under Read all orders scope header</li>
          <li>
            Enter the following for the reason
            "This is used to show reporting including year over year comparisons."
          </li>
          <li>Go back to the dev dashboard and reload the page</li>
          <li>Change the app url to https://cadence.upperquadrant.com</li>
          <li>Uncheck the Embed app in Shopify admin box.</li>
          <li>
            Paste the following scopes into the box under the Access heading
            <ul>
              <li>
                read_customers, read_inventory, read_locations, read_orders, read_products,
                read_all_orders
              </li>
            </ul>
          </li>
          <li>Click Release.</li>
          <li>In the modal window click Release again.</li>
          <li>Click on settings</li>
          <li>Copy the Client Id and Secret fields</li>
          <li>
            Setup all the fields in Cadence and make sure to send the validation email
            to the user.
          </li>
        </ol>
      </v-col>
      <v-col
        v-if="appTypeLocal === 'private'"
        cols="12"
        md="6"
      >
        To enable the pulling of data a private app must be created in the shopify store. Follow
        these steps to enable that.
        <ol>
          <li>
            Log in to your Shopify Store Admin account <a
              href="https://accounts.shopify.com/store-login"
              target="_blank"
            >here</a>.
          </li>
          <li>
            In the left menu bar, click Apps, and then, Manage private apps at the bottom of the
            next screen.
          </li>
          <li>
            If prompted click to enable creating private apps.
          </li>
          <li>
            Click Create new private app.
          </li>
          <li>
            In app details:
            <ol>
              <li>Provide a name for the private app (UQ Cadence).</li>
              <li>
                In Emergency developer email, enter the email address that Shopify should use
                for contacting you regarding your app (shopify@upperquadrant.com).
              </li>
            </ol>
          </li>
          <li>
            Under Admin API, click Show inactive Admin API permissions to view the list of Shopify
            API resources and the permissions you can configure for them.
          </li>
          <li>
            Specify Read Access permission for the following resources:
            <ul>
              <li>Customers</li>
              <li>Inventory</li>
              <li>Locations</li>
              <li>Orders</li>
              <li>Products</li>
            </ul>
          </li>
          <li>Click Save. Click Create app in the confirmation dialog box.</li>
          <li>Copy the API Key and Password fields into the boxes above.</li>
        </ol>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { debounce } from 'lodash';
import shopify from '../../../_api/shopify';
import Oauth from './Oauth';

export default {
  name: 'ShopifySource',
  components: {
    Oauth,
  },
  props: {
    id: {
      type: String,
      required: false,
      default: '',
    },
    isNew: {
      type: Boolean,
      required: false,
      default: false,
    },
    storeName: {
      type: String,
      required: false,
      default: '',
    },
    apiKey: {
      type: String,
      required: false,
      default: '',
    },
    password: {
      type: String,
      required: false,
      default: '',
    },
    ignoredInventoryLocations: {
      type: Array,
      required: false,
      default: () => [],
    },
    isGqlSalespersonFetchEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    appType: {
      type: String,
      required: false,
      default: 'dev',
    },
    accessToken: {
      type: String,
      required: false,
      default: '',
    },
    clientId: {
      type: String,
      required: false,
      default: '',
    },
    clientSecret: {
      type: String,
      required: false,
      default: '',
    },
    errorMessage: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      storeNameLocal: this.storeName,
      apiKeyLocal: this.apiKey,
      passwordLocal: this.password,
      locations: [],
      ignoredInventoryLocationsLocal: this.ignoredInventoryLocations,
      isGqlSalespersonFetchEnabledLocal: this.isGqlSalespersonFetchEnabled,
      appTypeLocal: this.appType,
      accessTokenLocal: this.accessToken,
      clientIdLocal: this.clientId,
      clientSecretLocal: this.clientSecret,
      appTypes: [
        'custom',
        'private',
        'dev',
      ],
    };
  },
  watch: {
    errorMessage() {
      if (this.errorMessage === 'InvalidAuth') {
        if (this.$refs.apiKeyValidation) {
          this.$refs.apiKeyValidation.setErrors(['Invalid Store Name, API Key, Password, or Access Level']);
        }
        if (this.$refs.accessTokenValidation) {
          this.$refs.accessTokenValidation.setErrors(['Invalid Store Name, Access Token, or Access Level']);
        }
        if (this.$refs.clientIdValidation) {
          this.$refs.clientIdValidation.setErrors(['Invalid Store Name, Access Token, or Access Level']);
        }
      }
    },
    async storeNameLocal() {
      await this.getLocations();
    },
    async apiKeyLocal() {
      await this.getLocations();
    },
    async passwordLocal() {
      await this.getLocations();
    },
    async appTypeLocal() {
      await this.getLocations();
      if (this.appTypeLocal === 'dev') {
        this.isGqlSalespersonFetchEnabledLocal = false;
      }
    },
    async accessTokenLocal() {
      await this.getLocations();
    },
    async clientIdLocal() {
      await this.getLocations();
    },
    async clientSecretLocal() {
      await this.getLocations();
    },
  },
  created() {
    this.getLocations();
    this.getLocations = debounce(this.getLocations, 1000);
  },
  methods: {
    getDetail() {
      let oauthDetail;
      try {
        oauthDetail = this.$refs.oauthControl.getDetail();
      } catch (e) {
        // ignore
      }

      return {
        storeName: this.storeNameLocal,
        apiKey: this.apiKeyLocal,
        password: this.passwordLocal,
        ignoredInventoryLocations: this.ignoredInventoryLocationsLocal,
        isGqlSalespersonFetchEnabled: this.isGqlSalespersonFetchEnabledLocal,
        appType: this.appTypeLocal,
        accessToken: this.accessTokenLocal,
        clientId: this.clientIdLocal,
        clientSecret: this.clientSecretLocal,
        ...oauthDetail,
      };
    },
    async getLocations() {
      if (this.storeNameLocal && ((this.appTypeLocal === 'private' && this.apiKeyLocal && this.passwordLocal)
        || (this.appTypeLocal === 'custom' && this.accessTokenLocal)
        || (this.appTypeLocal === 'dev' && this.isNew === false))) {
        try {
          const result = await shopify.getLocations(
            this.appTypeLocal,
            this.storeNameLocal,
            this.apiKeyLocal,
            this.passwordLocal,
            this.accessTokenLocal,
            this.clientIdLocal,
            this.clientSecretLocal,
            this.id,
          );
          this.locations = result.data;
        } catch (e) {
          if (e.response && e.response.data === 'InvalidAuth') {
            if (this.appTypeLocal === 'private') {
              this.$refs.apiKeyValidation.setErrors(['Invalid Store Name, API Key, Password, or Access Level']);
            } else if (this.appTypeLocal === 'custom') {
              this.$refs.accessTokenValidation.setErrors(['Invalid Store Name, Access Token, or Access Level']);
            } else {
              this.$refs.clientIdValidation.setErrors(['Invalid Store Name, Access Token, or Access Level']);
            }
          } else {
            throw (e);
          }
        }
      }
    },
  },
};
</script>
